import { useState } from 'react';
import {
  MonitorIcon,
  StopIcon,
  CircleNotchIcon,
  WarningIcon,
  CaretDownIcon,
  CaretUpIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Badge } from '~/components/ui/badge';
import { Tip } from '~/components/ui/tip';
import { formatUsdAsBlocks } from '~/features/limits/compute-blocks';
import { useShowComputeBlocks } from '~/features/limits/compute-blocks-display';
import { Button } from '~/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { Skeleton } from '~/components/ui/skeleton';
import { InlineMarkdown } from '~/components/inline-markdown';
import { MarkdownRenderer } from '~/components/markdown-renderer';
import {
  useOperatorRun,
  useOperatorRunForToolCall,
  type OperatorRunStepWire,
} from '~/hooks/queries/use-operator-runs';
import { useStopOperatorRun } from '~/hooks/mutations/use-stop-operator-run';
import { useTakeOverOperatorRun } from '~/hooks/mutations/use-take-over-operator-run';
import { useHandBackOperatorRun } from '~/hooks/mutations/use-hand-back-operator-run';
import { useOperatorTakeoverMutationsInFlight } from '~/features/operator/use-operator-takeover-mutations-in-flight';
import { useStopBoundaryTimer } from '~/features/operator/use-stop-boundary-timer';
import { humanTakeoverStepLabel } from '~/features/operator/human-takeover-step-label';
import {
  createHandBackHandler,
  createTakeOverHandler,
  createWatchDesktopHandler,
  deriveCardPhase,
  resolveFinalScreenshotUrl,
  resolveRunId,
  stopControl,
  takeoverControl,
  watchDesktopControl,
  type DesktopTaskCardPhase,
  type DesktopTaskToolOutput,
} from './desktop-task-card-model';

const MAX_STEPS_COLLAPSED = 6;

const PHASE_LABEL: Record<DesktopTaskCardPhase, string> = {
  // FRONT-1989 review F3: this phase covers BOTH "still waiting for the
  // person to approve the tool call" and "already approved, run not created
  // yet" (see deriveCardPhase's `toolState` fallback in
  // desktop-task-card-model.ts) — a neutral label, since the card cannot
  // tell which one it is.
  'awaiting-approval': 'Waiting to start',
  'not-started': 'Not started',
  starting: 'Starting…',
  running: 'Working on your screen…',
  // FRONT-1996 "Take the wheel".
  'needs-help': 'Needs your help',
  'in-control': "You're in control",
  // FRONT-2341: same secondary+spinner family as `running`/`starting`
  // below (see `phaseBadgeVariant`/the spinner condition) rather than the
  // amber "needs attention" treatment — this card reserves amber for the
  // two takeover phases above, and a pending stop is still fundamentally
  // "in progress", not something the person needs to act on.
  stopping: 'Stopping…',
  succeeded: 'Done',
  failed: 'Failed',
  stopped: 'Stopped',
  unavailable: "Couldn't load",
};

/** FRONT-1996: the two takeover phases get the app's single canonical
 *  amber "needs attention" treatment (CLAUDE.md's color rule), same
 *  pattern `operator-run-status.ts`/`operator-panel.tsx` already use for
 *  "running" — here scoped to just these two phases (unlike those, this
 *  card's plain `running` phase keeps its existing secondary+spinner
 *  look, unchanged). */
function phaseBadgeAmber(phase: DesktopTaskCardPhase): boolean {
  return phase === 'needs-help' || phase === 'in-control';
}

function phaseBadgeVariant(phase: DesktopTaskCardPhase): 'success' | 'destructive' | 'secondary' {
  if (phase === 'succeeded') return 'success';
  if (phase === 'failed' || phase === 'unavailable') return 'destructive';
  return 'secondary';
}

function extractGoal(input: unknown): string | undefined {
  if (input && typeof input === 'object' && !Array.isArray(input)) {
    const goal = (input as Record<string, unknown>).goal;
    if (typeof goal === 'string') return goal;
  }
  return undefined;
}

function formatTokenCount(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

function formatCostUsd(usd: number): string {
  return `$${usd.toFixed(usd < 0.01 ? 4 : 2)}`;
}

/**
 * The finished card's usage line. FRONT-2670: with the org's compute-blocks
 * switch on, tokens and dollars become one "~N CB" estimate (a dash when
 * the model has no price), and the tokens move into the hover hint.
 */
export function desktopTaskUsageLine(
  output: DesktopTaskToolOutput | undefined,
  showBlocks: boolean,
): { text: string; hint?: string } | null {
  if (output?.inputTokens == null && output?.outputTokens == null && output?.costUsd == null) return null;
  const steps = output?.stepCount != null ? `${output.stepCount} steps` : null;
  if (showBlocks) {
    const tokens =
      output?.inputTokens != null && output?.outputTokens != null
        ? `${output.inputTokens.toLocaleString('en-US')} input · ${output.outputTokens.toLocaleString('en-US')} output tokens.`
        : '';
    const priced = typeof output?.costUsd === 'number';
    return {
      text: [steps, priced ? formatUsdAsBlocks(output!.costUsd) : '—'].filter(Boolean).join(' · '),
      hint: `${priced ? 'Estimated compute for this task.' : 'Not priced yet: this model has no published price.'} ${tokens}`.trim(),
    };
  }
  return {
    text: [
      steps,
      output?.inputTokens != null && output?.outputTokens != null
        ? `${formatTokenCount(output.inputTokens + output.outputTokens)} tok`
        : null,
      output?.costUsd != null ? formatCostUsd(output.costUsd) : null,
    ]
      .filter(Boolean)
      .join(' · '),
  };
}

export interface DesktopTaskCardProps {
  sessionId: string;
  toolCallId: string;
  toolState: string;
  /** The tool call's raw input (`{goal: string}`) — `info.input` from
   *  `getToolInfo()`. Used only for the goal text before any run/output
   *  exists to read it from instead. */
  input: unknown;
  output: DesktopTaskToolOutput | undefined;
}

/**
 * FRONT-1989 — chat-transcript card for a `dispatch_desktop_task` run.
 *
 * Reads the operator run detail directly (`useOperatorRun`) rather than the
 * tool's own `output` — see `desktop-task-card-model.ts`'s module docblock
 * for why the tool's output alone can't drive a live view (the tool call
 * doesn't return until the whole run finishes, so `output` is always a
 * FINAL snapshot). This card owns the transition from "we don't have a
 * runId yet" (a fresh mount, before the toolCallId lookup has resolved) all
 * the way through a running task's steps to its finished summary, which is
 * why it holds its own hooks rather than being told which sub-state to
 * render — message-bubble.tsx's routing rule only decides WHETHER to mount
 * this card at all (an output with no runId at all is a refusal that never
 * reached the run store, and falls through to the plain `ToolCallCard`
 * instead).
 *
 * "Watch desktop" (FRONT-1989, once a runId is known) raises the shared
 * operator panel via `openOperatorPanel({ runId })` — the same overlay
 * FRONT-1991's Settings → Machine button opens with `{}` (no run), rendered
 * once in `app-layout.tsx` and reading whichever request was raised last.
 */
export function DesktopTaskCard({ sessionId, toolCallId, toolState, input, output }: DesktopTaskCardProps) {
  const goalFromInput = extractGoal(input);
  const [showAllSteps, setShowAllSteps] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Only look up the run by toolCallId while the tool's own output hasn't
  // told us the runId yet — once it has, the lookup is pure waste.
  const lookupEnabled = !output?.runId;
  const { data: lookupRun } = useOperatorRunForToolCall(sessionId, toolCallId, lookupEnabled);
  const runId = resolveRunId({ output, lookupRunId: lookupRun?.id });

  const { data: run, error: runError } = useOperatorRun(runId);
  const phase = deriveCardPhase({ toolState, output, run, runError });

  const stopMutation = useStopOperatorRun();
  const stop = stopControl(run, stopMutation.isPending);
  // FRONT-2341: forces one extra re-render at the 30s "taking longer than
  // usual" boundary — see that hook's own docblock for why an unchanged
  // poll response never does this on its own.
  useStopBoundaryTimer(run?.stopRequestedAt ?? null, stop.observedAt);
  const watchDesktop = watchDesktopControl({ runId, phase });

  const takeOverMutation = useTakeOverOperatorRun();
  const handBackMutation = useHandBackOperatorRun();
  // FRONT-1996 review M7: the SHARED mutation cache, not just this card's
  // own two mutation instances — see operator-panel.tsx's identical
  // comment on `useOperatorTakeoverMutationsInFlight`.
  const inFlight = useOperatorTakeoverMutationsInFlight(runId);
  const takeover = takeoverControl({
    run,
    takingOver: inFlight.takingOver,
    handingBack: inFlight.handingBack,
    stopping: stopMutation.isPending,
  });
  const onTakeOver = createTakeOverHandler({ runId, takeOver: (i) => takeOverMutation.mutate(i) });
  const onHandBack = createHandBackHandler({ runId, handBack: (i) => handBackMutation.mutate(i) });

  const goal = run?.goal ?? output?.goal ?? goalFromInput;
  const steps = run?.steps ?? [];
  // Collapsed view shows the NEWEST steps (the ones telling you what's
  // happening right now), never the first — a long-running task otherwise
  // freezes on its opening moves forever. `slice(-N)` on an array shorter
  // than N returns the whole array, so this is safe with any step count.
  const visibleSteps = showAllSteps ? steps : steps.slice(-MAX_STEPS_COLLAPSED);
  const hiddenCount = steps.length - visibleSteps.length;
  const modelLabel = run?.servedModel ?? output?.servedModel ?? run?.model ?? output?.model;
  const isFinished = phase === 'succeeded' || phase === 'failed' || phase === 'stopped';
  const finalScreenshotUrl = isFinished ? resolveFinalScreenshotUrl({ run, output }) : undefined;
  const showBlocks = useShowComputeBlocks();
  const usageLine = desktopTaskUsageLine(output, showBlocks);

  return (
    <div
      data-testid="desktop-task-card"
      data-run-id={runId ?? ''}
      data-phase={phase}
      className="border border-border rounded-lg overflow-hidden bg-card max-w-[420px]"
    >
      <div className="px-3 py-2 flex items-center gap-2 text-xs border-b border-border">
        <MonitorIcon size={14} className="text-muted-foreground shrink-0" />
        <span className="font-medium text-foreground truncate">Desktop task</span>
        {phaseBadgeAmber(phase) ? (
          <Badge className="ml-auto border-amber-500/30 bg-amber-500/15 text-amber-500">{PHASE_LABEL[phase]}</Badge>
        ) : (
          <Badge variant={phaseBadgeVariant(phase)} className="ml-auto">
            {(phase === 'running' || phase === 'stopping') && (
              <CircleNotchIcon size={11} className="animate-spin" />
            )}
            {PHASE_LABEL[phase]}
          </Badge>
        )}
      </div>

      <div className="p-3 space-y-2 text-xs">
        {goal && (
          // InlineMarkdown's prop type has no `data-testid`/arbitrary-attribute
          // escape hatch, so the height cap and test hook live on this wrapper
          // rather than on the component itself.
          <div
            data-testid="desktop-task-goal"
            className="max-h-60 overflow-y-auto break-words [overflow-wrap:anywhere]"
          >
            <InlineMarkdown text={goal} className="text-foreground" />
          </div>
        )}

        {modelLabel && (
          <div className="font-mono text-[length:var(--text-2xs)] text-muted-foreground truncate">
            {modelLabel}
          </div>
        )}

        {/* FRONT-1996 "Take the wheel": Astra is paused either because it
            asked for help or because the owner took over unprompted — both
            need the person's attention, so both get the amber treatment. */}
        {phase === 'needs-help' && run?.takeover && (
          <div
            data-testid="desktop-task-needs-help-banner"
            className="flex items-start gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 p-2 text-amber-500"
          >
            <WarningIcon size={13} className="shrink-0 mt-0.5" />
            <InlineMarkdown text={`Astra needs your help: ${run.takeover.reason ?? ''}`} />
          </div>
        )}
        {phase === 'in-control' && (
          <div
            data-testid="desktop-task-in-control-banner"
            className="flex items-start gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 p-2 text-amber-500"
          >
            <WarningIcon size={13} className="shrink-0 mt-0.5" />
            <span>You&apos;re in control. Astra is paused — use the desktop, then press Hand back.</span>
          </div>
        )}

        {/* No runId resolvable at all yet (a fresh mount, before the
            toolCallId lookup has resolved) — an awaiting-approval/starting
            skeleton, per message-bubble.tsx's routing rule. */}
        {!runId && (
          <div data-testid="desktop-task-card-skeleton" className="space-y-1.5 pt-1">
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        )}

        {/* runId known, but the detail query hasn't produced a run yet —
            loading branch keyed off the query's own in-flight state, never
            `!data` alone (CLAUDE.md FRONT-1525: `!data` reads as "loading"
            forever on a real fetch error), and a real error branch next to
            it. */}
        {runId && !run && (
          <>
            {runError ? (
              <div className="flex items-center gap-1.5 text-destructive" data-testid="desktop-task-card-error">
                <WarningIcon size={13} className="shrink-0" />
                <span>Couldn&apos;t load this desktop task.</span>
              </div>
            ) : (
              <div data-testid="desktop-task-card-loading" className="space-y-1.5 pt-1">
                <Skeleton className="h-3 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            )}
          </>
        )}

        {run && steps.length > 0 && (
          <ol className="space-y-1.5 pt-1" data-testid="desktop-task-card-steps">
            {visibleSteps.map((step) => (
              <DesktopTaskStepRow
                key={step.index}
                step={step}
                onOpenScreenshot={() => setLightboxIndex(step.index)}
              />
            ))}
          </ol>
        )}

        {run && hiddenCount > 0 && !showAllSteps && (
          <Button
            type="button"
            variant="ghost"
            size="xs"
            className="text-muted-foreground"
            onClick={() => setShowAllSteps(true)}
          >
            <CaretDownIcon size={12} /> Show all {steps.length} steps
          </Button>
        )}
        {run && showAllSteps && steps.length > MAX_STEPS_COLLAPSED && (
          <Button
            type="button"
            variant="ghost"
            size="xs"
            className="text-muted-foreground"
            onClick={() => setShowAllSteps(false)}
          >
            <CaretUpIcon size={12} /> Show fewer
          </Button>
        )}

        {/* Finished — show what the machine's screen actually looked like at
            the end, large: the run's own last step with a screenshot when
            the run detail has loaded, falling back to the tool output's
            `finalScreenshotUrl` (see resolveFinalScreenshotUrl's docblock). */}
        {isFinished && finalScreenshotUrl && (
          <img
            data-testid="desktop-task-final-screenshot"
            src={finalScreenshotUrl}
            alt="Final screenshot"
            className="w-full rounded-md object-contain outline outline-1 -outline-offset-1 outline-border bg-muted/30"
          />
        )}

        {run?.summary && isFinished && phase !== 'failed' && (
          <MarkdownRenderer content={run.summary} className="text-foreground pt-1" />
        )}

        {run?.error && phase === 'failed' && (
          <div className="flex items-start gap-1.5 text-destructive pt-1" data-testid="desktop-task-card-run-error">
            <WarningIcon size={13} className="shrink-0 mt-0.5" />
            <MarkdownRenderer content={run.error} className="text-destructive" />
          </div>
        )}

        {isFinished && usageLine && (
          <Tip label={usageLine.hint}>
            {/* Switch off: today's markup, byte for byte. */}
            {showBlocks ? (
              <div
                data-testid="desktop-task-card-usage"
                className="text-[length:var(--text-2xs)] text-muted-foreground pt-1 tabular-nums"
              >
                {usageLine.text}
              </div>
            ) : (
              <div className="text-[length:var(--text-2xs)] text-muted-foreground pt-1">{usageLine.text}</div>
            )}
          </Tip>
        )}
      </div>

      {(watchDesktop.show || takeover.takeOver.show || takeover.handBack.show || stop.show) && (
        <div className="px-3 py-2 border-t border-border flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
          {watchDesktop.show && (
            <Button
              type="button"
              data-testid="desktop-task-open-panel"
              variant="outline"
              size="xs"
              tooltip="Watch the live desktop this task is running on"
              onClick={createWatchDesktopHandler(watchDesktop)}
            >
              <MonitorIcon size={12} />
              Watch desktop
            </Button>
          )}
          {takeover.takeOver.show && (
            <Button
              type="button"
              data-testid="desktop-task-take-over"
              variant="outline"
              size="xs"
              disabled={takeover.takeOver.disabled}
              tooltip={takeover.takeOver.reason}
              onClick={onTakeOver}
            >
              {takeover.takeOver.disabled ? <CircleNotchIcon size={12} className="animate-spin" /> : null}
              {/* FRONT-1996 review M7: the label reuses the SAME reason
                  text the gate already computed for `disabled` — see
                  operator-panel.tsx's identical comment — rather than this
                  card's own `takeOverMutation.isPending`, so it reads
                  "Taking over…" the instant EITHER surface's click puts
                  this button into that disabled state. */}
              {takeover.takeOver.reason ?? 'Take over'}
            </Button>
          )}
          {takeover.handBack.show && (
            <Button
              type="button"
              data-testid="desktop-task-hand-back"
              variant="outline"
              size="xs"
              disabled={takeover.handBack.disabled}
              tooltip={takeover.handBack.reason}
              onClick={onHandBack}
            >
              {takeover.handBack.disabled ? <CircleNotchIcon size={12} className="animate-spin" /> : null}
              {takeover.handBack.reason ?? 'Hand back'}
            </Button>
          )}
          {stop.show && (
            <Button
              type="button"
              data-testid="desktop-task-stop"
              variant="destructive"
              size="xs"
              disabled={stop.disabled}
              onClick={() => runId && stopMutation.mutate({ runId })}
            >
              {stop.disabled ? (
                <CircleNotchIcon size={12} className="animate-spin" />
              ) : (
                <StopIcon size={12} weight="fill" />
              )}
              {stop.label}
            </Button>
          )}
        </div>
        {/* FRONT-2341: visible text, never a tooltip — a touch screen never
            sees a `tooltip` prop, and this is exactly the kind of thing a
            person needs to see without hovering anything. */}
        {stop.note && (
          <p data-testid="desktop-task-stop-note" className="text-[length:var(--text-2xs)] text-amber-500">
            {stop.note}
          </p>
        )}
        </div>
      )}

      <Dialog open={lightboxIndex !== null} onOpenChange={(open) => !open && setLightboxIndex(null)}>
        <DialogContent
          showCloseButton={false}
          aria-describedby={undefined}
          className="w-fit sm:max-w-[calc(100%-2rem)] gap-0 overflow-hidden p-0"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Desktop task screenshot</DialogTitle>
          </DialogHeader>
          {lightboxIndex !== null && (
            <div className="relative flex h-[82vh] max-h-[720px] w-[88vw] max-w-[960px] items-center justify-center bg-muted/30">
              <img
                src={steps.find((s) => s.index === lightboxIndex)?.screenshotUrl ?? ''}
                alt={`Step ${lightboxIndex + 1} screenshot`}
                className="h-full w-full object-contain outline outline-1 -outline-offset-1 outline-border"
              />
              <DialogClose asChild>
                <Button
                  variant="ghost"
                  size="icon-lg"
                  className="absolute top-2 right-2 rounded-full bg-black/50 text-white hover:bg-black/70 hover:text-white"
                >
                  <XIcon size={16} />
                  <span className="sr-only">Close</span>
                </Button>
              </DialogClose>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DesktopTaskStepRow({
  step,
  onOpenScreenshot,
}: {
  step: OperatorRunStepWire;
  onOpenScreenshot: () => void;
}) {
  return (
    <li className="flex gap-2 items-start" data-testid="desktop-task-step" data-step-index={step.index}>
      {step.screenshotUrl ? (
        <button type="button" onClick={onOpenScreenshot} className="shrink-0 group">
          <img
            src={step.screenshotUrl}
            alt={`Step ${step.index + 1}: ${step.label}`}
            loading="lazy"
            className="block w-14 h-9 object-cover rounded outline outline-1 -outline-offset-1 outline-border bg-muted/30 transition-opacity group-hover:opacity-90"
          />
        </button>
      ) : (
        <div className="shrink-0 w-14 h-9 rounded outline outline-1 -outline-offset-1 outline-border bg-muted/30" />
      )}
      <div className="min-w-0 flex-1">
        {/* FRONT-1996 review M9: a `human_takeover` step reads as one line
            ("You were in control · 1m 12s" / "Astra asked for help — you
            let it continue"), never label-then-note as two lines like an
            ordinary step — `humanTakeoverStepLabel` folds the step's own
            structured `takeoverDurationMs` into the label, so the raw
            `note` ("Paused for 1m 12s.") is never rendered again
            underneath it. Shared with `operator-transcript.tsx` so the two
            surfaces can't drift on this wording. */}
        {step.action === 'human_takeover' ? (
          <div className="text-foreground truncate" data-testid="desktop-task-takeover-step-label">
            {humanTakeoverStepLabel(step)}
          </div>
        ) : (
          <>
            <div className="text-foreground truncate">{step.label}</div>
            {step.note && <InlineMarkdown text={step.note} className="text-muted-foreground truncate" />}
          </>
        )}
      </div>
    </li>
  );
}
